import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import * as agentsApi from '../../api/agents';
import { setCurrentAgentSchedule } from './schedulesSlice';

// Async thunks
export const fetchAgentsThunk = createAsyncThunk(
  'agents/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await agentsApi.fetchAgents();
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch agents');
    }
  }
);

export const fetchAgentThunk = createAsyncThunk(
  'agents/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      return await agentsApi.fetchAgent(id);
    } catch (error) {
      return rejectWithValue(error.message || `Failed to fetch agent with ID ${id}`);
    }
  }
);

export const addAgentThunk = createAsyncThunk(
  'agents/add',
  async (agentData, { rejectWithValue }) => {
    try {
      return await agentsApi.addAgent(agentData);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add agent');
    }
  }
);

export const updateAgentThunk = createAsyncThunk(
  'agents/update',
  async (agentData, { rejectWithValue }) => {
    try {
      return await agentsApi.updateAgent(agentData);
    } catch (error) {
      return rejectWithValue(error.message || `Failed to update agent with ID ${agentData.id}`);
    }
  }
);

export const deleteAgentThunk = createAsyncThunk(
  'agents/delete',
  async (id, { rejectWithValue }) => {
    try {
      await agentsApi.deleteAgent(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || `Failed to delete agent with ID ${id}`);
    }
  }
);

export const syncFromHelpScoutThunk = createAsyncThunk(
  'agents/syncFromHelpScout',
  async (_, { rejectWithValue }) => {
    try {
      return await agentsApi.syncAgentsFromHelpScout();
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to sync agents from HelpScout');
    }
  }
);

export const fetchAgentWithScheduleThunk = createAsyncThunk(
  'agents/fetchWithSchedule',
  async ({ id, from, to }, { rejectWithValue, dispatch }) => {
    try {
      const result = await agentsApi.fetchAgentWithSchedule(id, from, to);

      // If the result includes schedule data, update the schedules slice
      if (result && result.schedule) {
        dispatch(setCurrentAgentSchedule(result.schedule));
      }

      return result;
    } catch (error) {
      return rejectWithValue(error.message || `Failed to fetch agent schedule for ID ${id}`);
    }
  }
);

const initialState = {
  list: [],
  currentAgent: null,
  loading: false,
  error: null,
  lastFetched: null,
};

const agentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    clearAgentErrors: (state) => {
      state.error = null;
    },
    clearCurrentAgent: (state) => {
      state.currentAgent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all agents
      .addCase(fetchAgentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchAgentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single agent
      .addCase(fetchAgentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAgent = action.payload;

        // Update agent in list if it exists
        const index = state.list.findIndex(agent => agent.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(fetchAgentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add agent
      .addCase(addAgentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAgentThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Assuming the API returns the new agent with ID
        state.list.push(action.payload);
      })
      .addCase(addAgentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update agent
      .addCase(updateAgentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAgentThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(agent => agent.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentAgent && state.currentAgent.id === action.payload.id) {
          state.currentAgent = action.payload;
        }
      })
      .addCase(updateAgentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete agent
      .addCase(deleteAgentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAgentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(agent => agent.id !== action.payload);
        if (state.currentAgent && state.currentAgent.id === action.payload) {
          state.currentAgent = null;
        }
      })
      .addCase(deleteAgentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Sync from HelpScout
      .addCase(syncFromHelpScoutThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncFromHelpScoutThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update state with sync results
        state.list = action.payload.agents || state.list;
        state.lastFetched = new Date().toISOString();
      })
      .addCase(syncFromHelpScoutThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch agent with schedule
      .addCase(fetchAgentWithScheduleThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentWithScheduleThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAgent = action.payload;
      })
      .addCase(fetchAgentWithScheduleThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAgentErrors, clearCurrentAgent } = agentsSlice.actions;

// Basic selectors
export const selectAllAgents = (state) => state.agents.list;
export const selectCurrentAgent = (state) => state.agents.currentAgent;
export const selectAgentsLoading = (state) => state.agents.loading;
export const selectAgentsError = (state) => state.agents.error;
export const selectAgentsLastFetched = (state) => state.agents.lastFetched;

// Memoized selectors
export const selectActiveAgents = createSelector(
  [selectAllAgents],
  (agents) => agents.filter(agent => agent.active)
);

export const selectAgentById = createSelector(
  [selectAllAgents, (state, id) => id],
  (agents, id) => agents.find(agent => agent.id === id)
);

export default agentsSlice.reducer;
